import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { useDownloadFile } from '@hooks/useDownloadFile';
import { Box, CircularProgress, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { canPreviewFile, getFileIcon, getFileIconColor, isImageFile, isVideoFile } from '@utils/fileHelpers';
import moment from 'moment';
import React, { useCallback, useState } from 'react';

interface FileCardProps {
    file: {
        _id: string;
        name: string;
        mimetype: string;
        size: number;
        url: string;
        thumbnailUrl?: string;
        createdAt: string;
        createdBy?: {
            _id: string;
            role: string;
            avatarUrl?: string;
        };
    };
    onDelete: (fileId: string, fileName: string) => void;
    onPreview?: (file: { _id: string; name: string; url: string }) => void;
    deleting?: boolean;
}

const FileCard: React.FC<FileCardProps> = ({ file, onDelete, onPreview, deleting }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { showSnackbar } = useSnackbar();
    const menuOpen = Boolean(anchorEl);

    const isImage = isImageFile(file.mimetype, file.name);
    const isVideo = isVideoFile(file.mimetype, file.name);
    const canPreview = canPreviewFile(file.mimetype, file.name);

    const { download, downloadFromUrl, loading: downloading } = useDownloadFile({
        onSuccess: url => {
            if (canPreview && onPreview) {
                onPreview({ _id: file._id, name: file.name, url });
            } else {
                downloadFromUrl(url, file.name);
            }
        },
        onError: error => {
            showSnackbar(error.message || 'Failed to download file', 'error');
        },
    });

    const handleMenuClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    }, []);

    const handleMenuClose = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        setAnchorEl(null);
    }, []);

    const handleDownload = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        download(file);
        handleMenuClose();
    }, [file, download, handleMenuClose]);

    const handleDeleteClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(file._id, file.name);
        handleMenuClose();
    }, [file._id, file.name, onDelete, handleMenuClose]);

    const fileIconClass = getFileIcon(file.mimetype, file.name);
    const fileIconColor = getFileIconColor(file.mimetype, file.name);

    return (
        <Box
            onClick={handleDownload}
            sx={{
                width: '100%',
                height: '187px',
                border: '1px solid #cbcbcb',
                borderRadius: '16px',
                overflow: 'hidden',
                backgroundColor: 'white',
                cursor: 'pointer',
                position: 'relative',
                "& .expand-icon-overlay": {
                    display: 'none',
                },
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                },
                "&:hover .expand-icon-overlay": {
                    display: 'flex',
                },
            }}>
            {/* Preview Area */}
            <Box
                sx={{
                    height: '123px',
                    borderBottom: '1px solid #cbcbcb',
                    backgroundColor: '#f9f9f9',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}>
                {isImage ? (
                    <Box
                        component="img"
                        src={file.thumbnailUrl || file.url}
                        alt={file.name}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                ) : isVideo ? (
                    <>
                        <video
                            width="100%"
                            height="100%"
                            style={{
                                backgroundColor: 'rgba(0, 0, 0, 1)',
                                borderRadius: '8px',
                                objectFit: 'cover',
                            }}>
                            <source src={file.url} type={file.mimetype} />
                            Video unsupported
                        </video>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                backgroundColor: 'rgba(0, 0, 0, 0)',
                                borderRadius: '45.803px',
                                padding: '7.763px',
                                width: '53px',
                                height: '53px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <Box
                                sx={{
                                    backgroundColor: 'white',
                                    borderRadius: '41.145px',
                                    padding: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <i className="fi fi-rr-play" style={{ fontSize: '24px', color: '#000000' }}></i>
                            </Box>
                        </Box>
                    </>
                ) : (
                    <Box
                        component="i"
                        className={fileIconClass}
                        sx={{
                            fontSize: '64px',
                            color: fileIconColor,
                        }}
                    />
                )}
            </Box>
            {/* Expand Icon Overlay on Hover */}
            <Box
                className="expand-icon-overlay"
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '123px',
                    backgroundColor: '#37104554',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 5,
                }}
            >
                {
                    canPreview ? (
                        <i className="fi fi-br-arrow-up-right-and-arrow-down-left-from-center" style={{ fontSize: '20px', color: '#FFFF' }} />
                    ) : <i className="fi fi-br-download" style={{ fontSize: '20px', color: '#FFFF' }} />
                }
            </Box>

            {/* File Info */}
            <Box
                sx={{
                    padding: '9px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '4px',
                }}>
                <Stack spacing={0.4} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: 'Plus Jakarta Sans',
                            fontSize: '13px',
                            fontWeight: 700,
                            lineHeight: '16px',
                            color: '#000000',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                        {file.name}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: 'Plus Jakarta Sans',
                            fontSize: '13px',
                            fontWeight: 500,
                            lineHeight: '16px',
                            color: '#939393',
                        }}>
                        {moment(file.createdAt).format('MMM D [at] hh:mm a')}
                    </Typography>
                </Stack>
                {file.createdBy?.avatarUrl && (
                    <Box
                        component="img"
                        src={file.createdBy.avatarUrl}
                        alt="Author"
                        sx={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            flexShrink: 0,
                            marginTop: '5px',
                        }}
                    />
                )}
            </Box>

            {/* Menu Button */}
            <IconButton
                onClick={handleMenuClick}
                disabled={deleting}
                sx={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '32px',
                    height: '32px',
                    padding: 0,
                    zIndex: 10,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                    },
                }}>
                <i className="fi fi-br-menu-dots" style={{ fontSize: '16px', color: '#000000' }}></i>
            </IconButton>

            {/* Dropdown Menu */}
            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        minWidth: '160px',
                    },
                }}>
                <MenuItem onClick={handleDownload} sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '14px' }}>
                    <i className="fi fi-br-download" style={{ fontSize: '16px', marginRight: '8px' }}></i>
                    Télécharger
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '14px', color: '#d32f2f' }}>
                    <i className="fi fi-br-trash" style={{ fontSize: '16px', marginRight: '8px' }}></i>
                    Supprimer
                </MenuItem>
            </Menu>

            {/* Download Loading Overlay */}
            {downloading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    )
}
export default FileCard;
